import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
         BarChart, Bar, ScatterChart, Scatter, PieChart, Pie, Cell,
         ComposedChart, Area, AreaChart } from 'recharts';
import { TrendingUp, Brain, Target, 
         Download, Eye, BarChart3, PieChart as PieIcon, 
         MessageSquare, Clock } from 'lucide-react';
import { getApiUrl } from '../api/api';

const AdvancedAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedMetric, setSelectedMetric] = useState('response_time');
  const [predictions, setPredictions] = useState([]);

  useEffect(() => {
    fetchAdvancedAnalytics();
    const interval = setInterval(fetchAdvancedAnalytics, 30000);
    return () => clearInterval(interval);
  }, [timeRange, selectedMetric]);

  const fetchAdvancedAnalytics = async () => {
    try {
      // Fetch dashboard stats first (we know this works)
      const dashboardRes = await fetch(getApiUrl('/api/dashboard/stats'));
      const dashboardStats = dashboardRes.ok ? await dashboardRes.json() : null;

      // Try to fetch new analytics data, fallback to mock data
      let incidentMetrics = generateMockIncidentAnalytics();
      let trends = generateMockTrends();
      let predictionData = generateMockPredictions();
      let ragMetrics = generateMockRAGMetrics();

      try {
        const metricsRes = await fetch(getApiUrl(`/api/analytics/metrics?range=${timeRange}`));
        if (metricsRes.ok) {
          analyticsMetrics = await metricsRes.json();
        }
      } catch (e) {
        console.warn('Analytics metrics fetch failed, using mock data');
      }

      try {
        const trendsRes = await fetch(getApiUrl(`/api/analytics/trends?range=${timeRange}`));
        if (trendsRes.ok) {
          trends = await trendsRes.json();
        }
      } catch (e) {
        console.warn('Analytics trends fetch failed, using mock data');
      }

      setAnalyticsData({
        incidents: incidentMetrics,
        trends: trends,
        predictions: predictionData,
        rag: ragMetrics,
        performanceMetrics: {
          incidentsProcessed: 2847,
          enrichmentRate: 89.2,
          avgResolutionTime: 2.4,
          ragAccuracy: 91.5
        },
        dashboardStats: dashboardStats
      });
      setPredictions(predictionData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Use all mock data on error
      setAnalyticsData({
        metrics: generateMockAnalytics(),
        trends: generateMockTrends(),
        correlations: generateMockCorrelations(),
        anomalyPatterns: generateAnomalyPatterns(),
        performanceMetrics: generatePerformanceMetrics(),
        predictiveInsights: generatePredictiveInsights()
      });
      setPredictions(generateMockPredictions());
      setLoading(false);
    }
  };

  const generateMockIncidentAnalytics = () => {
    const hours = Array.from({ length: 24 }, (_, i) => {
      const now = new Date();
      now.setHours(now.getHours() - (23 - i));
      return now.toISOString();
    });

    return {
      incidentFlow: hours.map((time, i) => ({
        timestamp: time,
        incoming: Math.floor(Math.random() * 15 + 5),
        enriched: Math.floor(Math.random() * 12 + 8),
        resolved: Math.floor(Math.random() * 10 + 6),
        escalated: Math.floor(Math.random() * 3 + 1)
      })),
      ragPerformance: {
        accuracy: 0.89,
        responseTime: 245,
        contextRelevance: 0.92,
        knowledgeBaseCoverage: 0.84
      },
      incidentCategories: [
        { category: 'Network Issues', count: 145, percentage: 28.5, avgResolutionTime: '2.3h' },
        { category: 'Application Errors', count: 122, percentage: 24.0, avgResolutionTime: '1.8h' },
        { category: 'Database Issues', count: 89, percentage: 17.5, avgResolutionTime: '3.1h' },
        { category: 'Security Alerts', count: 76, percentage: 14.9, avgResolutionTime: '4.2h' },
        { category: 'Infrastructure', count: 53, percentage: 10.4, avgResolutionTime: '2.7h' },
        { category: 'Performance', count: 25, percentage: 4.9, avgResolutionTime: '1.5h' }
      ]
    };
  };

  const generateMockTrends = () => ({
    weekly: [
      { day: 'Monday', incidents: 42, enriched: 38, resolved: 35, ragEfficiency: 89.2 },
      { day: 'Tuesday', incidents: 38, enriched: 36, resolved: 34, ragEfficiency: 91.5 },
      { day: 'Wednesday', incidents: 51, enriched: 45, resolved: 41, ragEfficiency: 87.8 },
      { day: 'Thursday', incidents: 29, enriched: 28, resolved: 26, ragEfficiency: 93.1 },
      { day: 'Friday', incidents: 47, enriched: 43, resolved: 39, ragEfficiency: 88.6 },
      { day: 'Saturday', incidents: 23, enriched: 22, resolved: 21, ragEfficiency: 95.7 },
      { day: 'Sunday', incidents: 31, enriched: 29, resolved: 27, ragEfficiency: 90.3 }
    ],
    monthly: [
      { month: 'Jan', incidents: 1240, enriched: 1156, resolved: 1089, mttr: 2.8 },
      { month: 'Feb', incidents: 1089, enriched: 1021, resolved: 976, mttr: 2.5 },
      { month: 'Mar', incidents: 1334, enriched: 1234, resolved: 1178, mttr: 2.3 }
    ]
  });

  const generateMockPredictions = () => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const time = new Date(now);
      time.setHours(time.getHours() + i);
      return {
        timestamp: time.toISOString(),
        predictedIncidents: Math.floor(Math.random() * 20 + 10),
        confidence: Math.random() * 0.3 + 0.7,
        enrichmentRate: Math.random() * 0.2 + 0.8,
        actualIncidents: i < 6 ? Math.floor(Math.random() * 20 + 10) : null
      };
    });
  };

  const generateMockRAGMetrics = () => ({
    modelPerformance: [
      { model: 'GPT-4o', accuracy: 92.5, avgResponseTime: 1.2, contextLength: 8192 },
      { model: 'Claude-3.5', accuracy: 89.8, avgResponseTime: 1.8, contextLength: 4096 },
      { model: 'Gemini-Pro', accuracy: 87.2, avgResponseTime: 0.9, contextLength: 2048 }
    ],
    enrichmentMetrics: [
      { type: 'Context Addition', success: 94.2, avgTime: '0.8s' },
      { type: 'Category Classification', success: 91.7, avgTime: '0.3s' },
      { type: 'Priority Assessment', success: 88.9, avgTime: '0.5s' },
      { type: 'Similar Incident Matching', success: 85.4, avgTime: '1.2s' }
    ]
  });

  const generateAnomalyPatterns = () => ({
    timePatterns: [
      { hour: 9, frequency: 0.15, type: 'peak_traffic' },
      { hour: 12, frequency: 0.22, type: 'lunch_spike' },
      { hour: 17, frequency: 0.28, type: 'evening_peak' },
      { hour: 2, frequency: 0.08, type: 'maintenance' }
    ],
    typeDistribution: [
      { name: 'Response Time', value: 45, color: '#8884d8' },
      { name: 'Error Rate', value: 25, color: '#82ca9d' },
      { name: 'Resource Usage', value: 20, color: '#ffc658' },
      { name: 'Network Issues', value: 10, color: '#ff7c7c' }
    ]
  });

  const generatePerformanceMetrics = () => ({
    detectionLatency: 85, // ms
    modelAccuracy: 94.2, // %
    throughput: 1250, // predictions/sec
    resourceUsage: 68 // % CPU
  });

  const generatePredictiveInsights = () => [
    {
      insight: "API Gateway likely to experience high latency in next 2 hours",
      confidence: 0.87,
      impact: "high",
      recommendation: "Consider scaling up instances preemptively"
    },
    {
      insight: "User service shows pattern indicating potential memory leak",
      confidence: 0.74,
      impact: "medium", 
      recommendation: "Schedule maintenance window for investigation"
    },
    {
      insight: "Payment service correlates with increased error rates during peak hours",
      confidence: 0.91,
      impact: "high",
      recommendation: "Implement circuit breaker pattern"
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Incident Analytics</h1>
          <p className="text-gray-600">Deep insights into incident management and RAG performance</p>
        </div>
        <div className="flex gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-purple-600" />
              Incidents Processed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.incidentsProcessed}</div>
            <p className="text-xs text-green-600">+12% vs last week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Brain className="h-4 w-4 text-blue-600" />
              Enrichment Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.enrichmentRate}%</div>
            <p className="text-xs text-green-600">+3.2% vs last week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Avg Resolution Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.avgResolutionTime}h</div>
            <p className="text-xs text-green-600">-0.3h vs last week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              RAG Accuracy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.ragAccuracy}%</div>
            <p className="text-xs text-green-600">+1.8% vs last week</p>
          </CardContent>
        </Card>
      </div>

      {/* Incident Flow Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Incident Flow & Processing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={analyticsData.incidents?.incidentFlow || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(time) => new Date(time).toLocaleTimeString()}
              />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                labelFormatter={(time) => new Date(time).toLocaleString()}
              />
              <Bar yAxisId="left" dataKey="incoming" fill="#8884d8" name="Incoming Incidents" />
              <Bar yAxisId="left" dataKey="enriched" fill="#82ca9d" name="Enriched Incidents" />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="resolved" 
                stroke="#ff7300" 
                strokeWidth={2}
                name="Resolved Incidents"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Incident Categories and RAG Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieIcon className="h-5 w-5 text-orange-600" />
              Incident Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.incidents?.incidentCategories || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({category, percentage}) => `${category}: ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {(analyticsData.incidents?.incidentCategories || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={[
                      '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'
                    ][index % 6]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name, props) => [
                  `${value} incidents (${props.payload.percentage}%)`,
                  props.payload.category
                ]} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              RAG Model Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-blue-50 p-3 rounded">
                  <div className="font-semibold text-blue-900">Accuracy</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {analyticsData.incidents?.ragPerformance?.accuracy * 100 || 89}%
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <div className="font-semibold text-green-900">Response Time</div>
                  <div className="text-2xl font-bold text-green-600">
                    {analyticsData.incidents?.ragPerformance?.responseTime || 245}ms
                  </div>
                </div>
                <div className="bg-purple-50 p-3 rounded">
                  <div className="font-semibold text-purple-900">Context Relevance</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {(analyticsData.incidents?.ragPerformance?.contextRelevance * 100) || 92}%
                  </div>
                </div>
                <div className="bg-orange-50 p-3 rounded">
                  <div className="font-semibold text-orange-900">Knowledge Coverage</div>
                  <div className="text-2xl font-bold text-orange-600">
                    {(analyticsData.incidents?.ragPerformance?.knowledgeBaseCoverage * 100) || 84}%
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Predictions and Enrichment Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-purple-600" />
              Incident Volume Prediction
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={predictions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(time) => new Date(time).toLocaleTimeString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(time) => new Date(time).toLocaleString()}
                />
                <Area 
                  type="monotone" 
                  dataKey="predictedIncidents" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.3}
                  name="Predicted Incidents"
                />
                <Line 
                  type="monotone" 
                  dataKey="actualIncidents" 
                  stroke="#ff7c7c" 
                  strokeWidth={2}
                  name="Actual Incidents"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Enrichment Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData.rag?.enrichmentMetrics?.map((metric, index) => (
                <div key={metric.type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{metric.type}</div>
                    <div className="text-sm text-gray-600">
                      Avg Time: {metric.avgTime}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div 
                      className={`w-3 h-3 rounded-full ${
                        metric.success > 95 ? 'bg-green-500' : 
                        metric.success > 90 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                    />
                    <span className="font-bold">{metric.success}%</span>
                  </div>
                </div>
              )) || []}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics sections cleaned up - removed Anomaly Type Distribution, Predictive Insights, and Weekly Performance Trends */}
    </div>
  );
};

export default AdvancedAnalytics;